import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Heading, Flex, SimpleGrid, Button, Divider, Spacer, useDisclosure, Box } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { Formik, Form } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { editItem, checkItemInputEvent } from '../../services/items';
import { checkCoordsDigits } from '../../services/portfolios';
import { deleteHedgeItem } from '../../services/hedges';

import { loadModules } from 'esri-loader';

import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';

import Tooltip from '../utils/Tooltip';
import ConfirmationModal from '../utils/ConfirmationModal';
import ItemPropertiesModal from '../utils/ItemPropertiesModal';
import CircularIconButton from '../utils/CircularIconButton';
import SecondaryIconButton from '../utils/SecondaryIconButton';

import InputField from '../forms/InputField';
import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

import useCommonToast from '../../hooks/useCommonToast';

import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as FileIcon } from '../../icons/file.svg';
import { ReactComponent as HedgeIcon } from '../../icons/item-icons/hedges-and-contracts.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as DeleteIcon } from '../../icons/delete.svg';

const TopContent = ({
    portfolio,
    hedge,
    onHedgeDelete,
    onHedgeEdit,
    onNodeOwnershipPercentageUpdate,
    refreshMapLayer,
}) => {
    const intl = useIntl();
    const propertiesModal = useDisclosure();
    const deleteItemModal = useDisclosure();
    const toast = useCommonToast();

    const [inEditDescription, setInEditDescription] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState({
        description: hedge.description,
        bookId: hedge.bookId,
        itemTypeId: hedge.itemTypeId,
        latitude: hedge?.latitude !== null ? hedge?.latitude : '',
        longitude: hedge?.longitude !== null ? hedge?.longitude : '',
    });

    const books = useSelector((state) => state.item.dataForItemCreation.books);
    const itemTypes = useSelector((state) => state.item.itemTypes);

    const financialAndPhysicalInstruments = itemTypes.filter(
        (itemType) => itemType.description === 'Financial Instrument' || itemType.description === 'Physical Instrument'
    );

    const formLabels = {
        book: intl.formatMessage({ id: 'hedges_book' }),
        financialPhysical: intl.formatMessage({ id: 'hedges_modal_item_type_label' }),
        powerSimmID: intl.formatMessage({ id: 'hedge_powersimm_id' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
        longitude: intl.formatMessage({ id: 'longitude' }),
    };

    const onHedgeFormSubmit = async ({ bookId, description, itemTypeId, latitude, longitude }) => {
        const successMessage = intl.formatMessage({ id: 'hedges_save_changes_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        const [webMercatorUtils] = await loadModules(['esri/geometry/support/webMercatorUtils']);

        const [x, y] = webMercatorUtils.lngLatToXY(longitude, latitude);

        try {
            await editItem(hedge.id, {
                bookId,
                description,
                itemTypeId,
                pricingPointId: null,
                longitude: longitude !== '' ? longitude : null,
                latitude: latitude !== '' ? latitude : null,
                x: longitude !== '' ? x : null,
                y: latitude !== '' ? y : null,
            });

            setInitialFormValues({
                description,
                bookId,
                itemTypeId,
                longitude,
                latitude,
            });

            onHedgeEdit({ id: hedge.id, description, bookId });
            setInEditDescription(false);
            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
        if (typeof refreshMapLayer === 'function') {
            refreshMapLayer({ long: longitude, lat: latitude, objectRefId: hedge.objectRefId });
        }
    };

    const onEditDescription = (isValid) => {
        if (!isValid) {
            return;
        }
        setInEditDescription((prev) => !prev);
    };

    const validateDescription = (value) => {
        const trimmed = value.trim();
        const label = intl.formatMessage({ id: 'hedge_label_name' });

        if (trimmed.length === 0) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        }

        if (trimmed.length > 100) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label, lengthRule: '100 characters or less' }
            );
        }
    };

    const validate = (values) => {
        let longitude = validateLongLat(values.longitude, 'long', values.latitude);
        let latitude = validateLongLat(values.latitude, 'lat', values.longitude);

        return {
            ...(longitude ? { longitude } : null),
            ...(latitude ? { latitude } : null),
        };
    };

    const validateLongLat = (val, type, sibling) => {
        let errorMessage;

        if (type === 'long') {
            if (val >= 180 || val <= -180) {
                errorMessage = intl.formatMessage({ id: 'longitude_validation_borders' });
            }
        }
        if (type === 'lat') {
            if (val > 85 || val < -85) {
                errorMessage = intl.formatMessage({ id: 'latitude_validation_borders' });
            }
        }

        if (val !== '' && sibling === '') {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_both' });
        }

        let digitsError = checkCoordsDigits(val);
        if (digitsError) {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_decimal' });
        }

        return errorMessage;
    };

    const onItemDelete = async (currentHedge) => {
        try {
            const { id } = currentHedge;
            await deleteHedgeItem(id);
            onHedgeDelete(id);

            const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'hedge item' });
            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    return (
        <>
            <Formik
                validateOnChange={false}
                enableReinitialize
                initialValues={initialFormValues}
                onSubmit={onHedgeFormSubmit}
                validate={validate}
            >
                {({ isSubmitting, dirty, isValid, values }) => (
                    <Form>
                        <Flex pt={3} pl={6} align="center">
                            <StyledHedgeIcon isMaster={hedge.securityId} />

                            {!inEditDescription ? (
                                <Heading px={2} as="h2" variant="h2">
                                    {values.description}
                                </Heading>
                            ) : (
                                <InputFormikField
                                    name="description"
                                    px={3}
                                    type="text"
                                    flex="1"
                                    maxW="500px"
                                    validate={validateDescription}
                                    onKeyDown={checkItemInputEvent}
                                    isFastField
                                />
                            )}

                            <Box display="inline-block" mr={4}>
                                <Tooltip label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}>
                                    <CircularIconButton
                                        aria-label={intl.formatMessage({
                                            id: 'common_edit_btn_tooltip_and_label',
                                        })}
                                        leftIcon={<EditIcon />}
                                        onClick={() => onEditDescription(isValid)}
                                    />
                                </Tooltip>
                            </Box>
                        </Flex>

                        <ActionBar>
                            <ShowHideSidebarButton />

                            <SecondaryIconButton
                                leftIcon={<FileIcon />}
                                size="sm"
                                variant="solid"
                                flexShrink={0}
                                onClick={propertiesModal.onOpen}
                            >
                                <FormattedMessage id="portfolio_item_properties" />
                            </SecondaryIconButton>

                            <Spacer display={{ base: 'none', xl: 'block' }} />

                            <Button
                                onClick={deleteItemModal.onOpen}
                                leftIcon={<DeleteIcon />}
                                type="button"
                                variant="special"
                                size="sm"
                            >
                                <FormattedMessage id="common_delete_item_btn" />
                            </Button>

                            <Button
                                leftIcon={<SaveIcon />}
                                isDisabled={!isValid || !dirty}
                                isLoading={isSubmitting}
                                type="submit"
                                size="sm"
                            >
                                <FormattedMessage id="common_save_changes_button" />
                            </Button>
                        </ActionBar>

                        <Divider />

                        <SimpleGrid pt={7} pb={5} px={6} columns={{ xl: 3 }} spacingX={6} spacingY={4}>
                            <ComboboxFormikField
                                id="bookId"
                                name="bookId"
                                label={formLabels.book}
                                options={books}
                                valueKey="id"
                                labelKey="name"
                            />

                            <ComboboxFormikField
                                id="itemTypeId"
                                name="itemTypeId"
                                label={formLabels.financialPhysical}
                                options={financialAndPhysicalInstruments}
                                valueKey="itemTypeId"
                                labelKey="description"
                            />

                            <InputField
                                id="powerSimmID"
                                name="powerSimmID"
                                label={formLabels.powerSimmID}
                                value={hedge.id}
                                isDisabled
                            />

                            <InputFormikField
                                id="latitude"
                                name="latitude"
                                label={formLabels.latitude}
                                type="number"
                                isFastField
                            />

                            <InputFormikField
                                id="longitude"
                                name="longitude"
                                label={formLabels.longitude}
                                type="number"
                                isFastField
                            />
                        </SimpleGrid>

                        <Divider />
                    </Form>
                )}
            </Formik>

            {propertiesModal.isOpen && (
                <ItemPropertiesModal
                    isOpen
                    onClose={propertiesModal.onClose}
                    itemId={hedge.id}
                    type="hedge"
                    portfolio={portfolio}
                    onNodeOwnershipPercentageUpdate={onNodeOwnershipPercentageUpdate}
                />
            )}

            {deleteItemModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={deleteItemModal.onClose}
                    header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Item' }} />}
                    hasExtraStep
                    content={<FormattedMessage id="common_delete_modal_msg" values={{ item: hedge.description }} />}
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_delete" />
                        </Box>
                    }
                    onConfirm={() => {
                        onItemDelete(hedge);
                        deleteItemModal.onClose();
                    }}
                />
            )}
        </>
    );
};

const StyledHedgeIcon = styled(HedgeIcon, { shouldForwardProp: (prop) => prop !== 'isMaster' })`
    height: 32px;
    width: 32px;
    flex-shrink: 0;

    > path {
        fill: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    }
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

export default TopContent;
